/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useState, useEffect, useCallback } from 'react';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';

/**
 * Get all changes on the entered servicing loan since the loan went to servicing.
 * @param loanNumber the loan number we want the history of
 * @returns
 */
const useGetServicingHistory = (loanNumber: string) => {
  const [history, setHistory] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const { token } = useAuthContext();

  const { reset } = useAbortController();

  const getServicingHistory = useCallback(async () => {
    if (!token || !loanNumber) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    const url = process.env.REACT_APP_SERVICING_HISTORY.replace('?', loanNumber);

    const controller = reset();
    const config = { signal: controller.signal };

    const headers = {
      'Session-Token': token,
      'Content-Type': 'application/json',
    };

    try {
      const response = await axiosCall({ url: url, method: 'get' }, config, headers);
      setHistory(response);
      setLoading(false);
    } catch (error) {
      setErrorMessage('Error Loading Servicing History.');
      setLoading(false);
      console.error(error.message);
    }
  }, [loanNumber, reset, token]);

  useEffect(() => {
    getServicingHistory();
  }, [getServicingHistory]);

  return {
    servicingHistory: history,
    servicingHistoryErrorMessages: errorMessage,
    loadingServicingHistory: loading,
  };
};

export default useGetServicingHistory;
